// @flow
import type { Product as ProductType } from "../../lib/Types";
import React, { Fragment } from "react";
import styled from "styled-components";
import { DefaultContainer } from "./Theme";
import ScheduleList from "./ScheduleList";
import Expander from "../Expander";
import ProductBarcodes from "./ProductBarcodes";
import Button from "../Button";
import ProductSchedulesForm from "../products/ProductSchedulesForm";
import TestingProtocols from "./TestingProtocols";

// Styled Components
const Header = styled.div`
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 5px;
  user-select: none;
`;

const ProductContainer = styled(DefaultContainer)`
  margin-bottom: 4px;
  border-radius: 0px;
  ${Header}:hover {
    text-decoration: underline;
    background-color: #fafafa !important;
  }
`;

const DefaultsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-left: 20px;
  padding-top: 8px;
  font-size: 0.9em;
  color: #666;
  align-items: center;
`;

const DefaultsLabel = styled.span`
  font-weight: bold;
  text-transform: uppercase;
  font-size: 0.8em;
  margin-right: 12px;
  color: #999;
  border-right: 1px solid #eee;
  padding-right: 12px;
  margin-bottom: 4px;
`;

const DefaultItem = styled.div`
  margin-right: 16px;
  margin-bottom: 4px;
  span {
    font-weight: bold;
    margin-right: 4px;
  }
`;

const ActionToolbar = styled.div`
  padding-left: 20px;
  padding-top: 20px;
`;

const ProtocolsPanel = styled.div`
  margin-top: 20px;
  margin-bottom: 20px;
`;

// Sub-components
const ProductDefaults = ({ defaults }) => {
  if (defaults.length === 0) return null;

  return (
    <DefaultsContainer>
      <DefaultsLabel>Defaults</DefaultsLabel>
      {defaults.map((defaultValue) => (
        <DefaultItem key={defaultValue.client_list_name}>
          <span>{defaultValue.client_list_name}:</span>
          {defaultValue.default_value}
        </DefaultItem>
      ))}
    </DefaultsContainer>
  );
};

const ActionButton = ({ title, onClick }) => (
  <div className="btn-group">
    <Button title={title} tiny icon="fa-plus" onClick={onClick} />
  </div>
);

// Types
type Props = {
  product: ProductType,
  onlyPick: boolean,
  pickSchedule: Function,
  registerProtocol: Function,
  companyId: number,
  addSchedules: boolean,
  initialExpanded?: boolean,
};

type State = {
  expanded: boolean,
  showBarcodeForm: boolean,
  showScheduleForm: boolean,
  showProtocols: boolean,
  scheduleGeneration: number,
};

export default class Product extends React.Component<Props, State> {
  state = {
    expanded: this.props.initialExpanded || false,
    showBarcodeForm: false,
    showScheduleForm: false,
    showProtocols: false,
    scheduleGeneration: 1,
  };

  toggleExpand = () => this.setState({ expanded: !this.state.expanded });

  toggleBarcodeForm = () =>
    this.setState({ showBarcodeForm: !this.state.showBarcodeForm });

  toggleScheduleForm = () =>
    this.setState({ showScheduleForm: !this.state.showScheduleForm });

  toggleProtocols = () =>
    this.setState({ showProtocols: !this.state.showProtocols });

  closeBarcodeForm = () => this.setState({ showBarcodeForm: false });

  incrementScheduleGeneration = () =>
    this.setState({ scheduleGeneration: this.state.scheduleGeneration + 1 });

  pickSchedule = (test_schedule, count) => {
    const { product } = this.props;
    this.props.pickSchedule({ product, test_schedule, count });
  };

  registerProtocol = (protocol_id) => {
    const { product } = this.props;
    this.props.registerProtocol({ product, protocol_id });
  };

  renderActionButtons() {
    const { addSchedules } = this.props;

    return (
      <ActionToolbar className="btn-toolbar">
        <ActionButton title="Add Barcode" onClick={this.toggleBarcodeForm} />
        {addSchedules && (
          <ActionButton title="Add Schedule" onClick={this.toggleScheduleForm} />
        )}
        <ActionButton title="Register Protocol" onClick={this.toggleProtocols} />
      </ActionToolbar>
    );
  }

  renderBarcodeForm() {
    const { product } = this.props;
    const { showBarcodeForm } = this.state;

    if (!showBarcodeForm) return null;

    return (
      <ProductBarcodes
        product_id={product.id}
        isFormVisible={showBarcodeForm}
        closeForm={this.closeBarcodeForm}
      />
    );
  }

  renderScheduleForm() {
    const { product, companyId } = this.props;
    const { showScheduleForm } = this.state;

    if (!showScheduleForm) return null;

    return (
      <ProductContainer style={{ margin: 20 }}>
        <ProductSchedulesForm
          product={product}
          company_id={companyId}
          ref={(ref) => (this.productScheduleForm = ref)}
          toggleScheduleForm={this.toggleScheduleForm}
          reloadProduct={this.incrementScheduleGeneration}
        />
      </ProductContainer>
    );
  }

  renderProtocolsPanel() {
    const { companyId } = this.props;
    const { showProtocols } = this.state;

    if (!showProtocols) return null;

    return (
      <ProtocolsPanel className="basic-panel with-padding">
        <h4>Select Protocol</h4>
        <TestingProtocols
          company_id={companyId}
          registerProtocol={this.registerProtocol}
          onClose={() => this.setState({ showProtocols: false })}
        />
      </ProtocolsPanel>
    );
  }

  renderExpandedContent() {
    const { product, onlyPick } = this.props;
    const { scheduleGeneration } = this.state;

    return (
      <Fragment>
        <ProductDefaults defaults={product.default_product_field_values} />
        {this.renderActionButtons()}
        {this.renderBarcodeForm()}
        {this.renderScheduleForm()}
        {this.renderProtocolsPanel()}
        <ScheduleList
          key={scheduleGeneration}
          product_id={product.id}
          onlyPick={onlyPick}
          pickSchedule={this.pickSchedule}
        />
      </Fragment>
    );
  }

  render() {
    const { product } = this.props;
    const { expanded } = this.state;

    return (
      <ProductContainer>
        <Header onClick={this.toggleExpand}>
          <Expander expanded={expanded} />
          <span>{product.name}</span>
        </Header>
        {expanded && this.renderExpandedContent()}
      </ProductContainer>
    );
  }
}
